import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { rolesService, usersService } from '@/services/apiClient';
import type { UserRole } from '@/types/roles';
import type { User } from '@/types/user';

export const useUser = (
  userId: string,
  token: string,
  enabled = true
): UseQueryResult<User, Error> => {
  return useQuery<User, Error>({
    queryKey: ['user', userId],
    queryFn: () => usersService.getUser(userId),
    enabled: enabled && !!userId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUserRoles = (
  userId: string,
  token: string,
  enabled = true
): UseQueryResult<UserRole[], Error> => {
  return useQuery<UserRole[], Error>({
    queryKey: ['userRoles', userId],
    queryFn: () => rolesService.getUserRoles(userId),
    enabled: enabled && !!userId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Combined hook to get user info and roles together
export const useUserWithRoles = (userId: string, token: string, enabled = true) => {
  const userQuery = useUser(userId, token, enabled);
  const rolesQuery = useUserRoles(userId, token, enabled);

  const isLoading = userQuery.isLoading || rolesQuery.isLoading;
  const isError = userQuery.isError || rolesQuery.isError;
  const error = userQuery.error || rolesQuery.error;

  // Transform the data to match our internal user format
  const transformedUser =
    userQuery.data && rolesQuery.data
      ? {
          id: userQuery.data.id,
          name: userQuery.data.name,
          email: userQuery.data.email,
          role: rolesQuery.data[0]?.role.name || 'employee',
          created_at: userQuery.data.createdAt || userQuery.data.created_at,
          updated_at: userQuery.data.updatedAt || userQuery.data.updated_at,
          organization: userQuery.data.organization,
          organizationId: userQuery.data.organizationId,
        }
      : null;

  const userContext = rolesQuery.data
    ? {
        roles: rolesQuery.data.map(role => ({
          id: role.role.id,
          name: role.role.name,
          organizationId: role.role.organizationId,
        })),
        organizationIds: rolesQuery.data.map(role => role.role.organizationId),
        isSuperAdmin: rolesQuery.data.some(role => role.role.name === 'super_admin'),
      }
    : null;

  const refetch = (): void => {
    void userQuery.refetch();
    void rolesQuery.refetch();
  };

  return {
    user: transformedUser,
    userContext,
    isLoading,
    isError,
    error,
    refetch,
  };
};
